import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from "../app/store";
import { ApiEndpoints } from '../api/endpoints';
import { api, handleLogoutUser } from '../utils/utils';
import { useNavigate } from 'react-router-dom';
import { Box } from '../modal/box';
import User from '../modal/User';


// Define the Coupon interface
export interface Coupon {
  id: string;
  box_id: string;
  code: string;
  created_by: string;
  created_at: string;
  redeemed: boolean;
  redeemed_by: string;
  active: boolean;
  type: string;
  box_details: Box;
  user_created_by: User;
  user_redeemed_by: User;
}

// Define the initial state
interface CouponsState {
  isLoading: boolean;
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  coupons: Coupon[];
  isNeedToRefresh: boolean;
  generateCouponsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  generateCouponsError: string | null;
  isGenerateCouponsInProgress: boolean;
  attachBoxStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  attachBoxError: string | null;
  deleteCouponStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  deleteCouponError: string | null;
  isDeleteCouponInprogress: boolean;
  deleteCouponId: string | null;
}

const initialState: CouponsState = {
  isLoading: false,
  isNeedToRefresh: false,
  currentPage: 1,
  itemsPerPage: 20,
  totalItems: 0,
  coupons: [],
  generateCouponsStatus: 'idle',
  generateCouponsError: null,
  isGenerateCouponsInProgress: false,
  attachBoxStatus: 'idle',
  attachBoxError: null,
  deleteCouponStatus: 'idle',
  deleteCouponError: null,
  isDeleteCouponInprogress: false,
  deleteCouponId: null,
};

// Async action to attach a box to a coupon
export const attachBoxToCoupon = createAsyncThunk<
  { coupon_id: string; box_id: string },
  { coupon_id: string; box_id: string },
  { dispatch: AppDispatch; rejectValue: string }
>(
  'coupons/attachBoxToCoupon',
  async ({ coupon_id, box_id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`/coupons/attach-box?coupon_id=${coupon_id}&box_id=${box_id}`);
      dispatch(getCouponsContent());

      return { coupon_id, box_id };
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.detail);
      }
      return rejectWithValue('Failed to attach box to coupon.');
    }
  }
);


// Async action to generate coupons
export const generateCoupons = createAsyncThunk<
  { created: number; coupons: Coupon[] },
  number,
  { dispatch: AppDispatch; rejectValue: string }
>(
  'coupons/generateCoupons',
  async (count, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`/coupons/generate?count=${count}`);
      dispatch(getCouponsContent());

      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.detail);
      }
      return rejectWithValue('Failed to generate coupons.');
    }
  }
);

// Define async thunks with proper typing
export const getCouponsContent = createAsyncThunk<
  any, // The return type of the thunk
  void, // The first argument to the thunk
  { dispatch: AppDispatch, state: RootState } // ThunkAPI type
>(
  "/items/content",
  async (_, { dispatch, getState }) => {
    try {
      const couponsState = getState().coupons; // Adjusted to match slice name

      if (!couponsState) {
        console.error("coupons state is not defined");
        return;
      }

      const { currentPage, itemsPerPage } = couponsState;
      const offset = (currentPage - 1) * itemsPerPage;

      const queryParams: any = {
        offset,
        limit: itemsPerPage,
      };

      const response = await api.get(ApiEndpoints.COUPONS, {
        params: queryParams,
      });
      return response.data;
    } catch (error: any) {
      console.error(error);
      // Check if the error is a 401
      if (error.response && error.response.status === 401) {
        // Dispatch the logout action
        handleLogoutUser(dispatch, useNavigate());
      }
      throw error;
    }
  }
);


// Async action to delete a banner
export const deleteCoupon = createAsyncThunk<
  void,
  string,
  { dispatch: AppDispatch, rejectValue: string }
>(
  'setting/deleteWallet',
  async (couponId, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.delete(`/coupons/${couponId}`);
      dispatch(getCouponsContent());
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.detail);
      }
      return rejectWithValue('Failed to delete coupon');
    }
  }
);


const couponsSlice = createSlice({
  name: 'coupons',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
      state.isNeedToRefresh = true;
    },

    resetAttachBoxToCouponStatus: (state) => {
      state.attachBoxStatus = 'idle';
      state.attachBoxError = null;
    },
    resetDeleteWalletStatus: (state) => {
      state.deleteCouponStatus = 'idle';
      state.deleteCouponError = null;
      state.isDeleteCouponInprogress = false;
      state.deleteCouponId = null;
    },
    resetGenerateCouponsStatus: (state) => {
      state.generateCouponsStatus = 'idle';
      state.generateCouponsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCouponsContent.pending, (state) => {
        state.isLoading = true;
        state.isNeedToRefresh = false;
      })
      .addCase(getCouponsContent.fulfilled, (state, action) => {
        console.log(action.payload.list);

        const usersMap = new Map<string, User>();
        (action.payload.users || []).forEach((user: User) => {
          usersMap.set(user.id, user);
        });

        state.coupons = action.payload.list.map((coupon: Coupon) => {
          const createdByUser = usersMap.get(coupon.created_by);
          const redeemedByUser = usersMap.get(coupon.redeemed_by);

          return {
            ...coupon,
            box_details: coupon.box_details || {} as Box,
            user_created_by: createdByUser ? createdByUser : {} as User,
            user_redeemed_by: redeemedByUser ? redeemedByUser : {} as User,
          };
        });
        
        state.totalItems = action.payload.total;
        state.isLoading = false;
        state.isNeedToRefresh = false;
      })
      .addCase(getCouponsContent.rejected, (state) => {
        state.isLoading = false;
        state.isNeedToRefresh = false;
      })
      .addCase(generateCoupons.pending, (state) => {
        state.generateCouponsStatus = 'loading';
        state.generateCouponsError = null;
        state.isGenerateCouponsInProgress = true;
      })
      .addCase(generateCoupons.fulfilled, (state, action) => {
        state.generateCouponsStatus = 'succeeded';
        state.isGenerateCouponsInProgress = false;
      })
      .addCase(generateCoupons.rejected, (state, action) => {
        state.generateCouponsStatus = 'failed';
        state.generateCouponsError = action.payload || 'Failed to generate coupons.';
        state.isGenerateCouponsInProgress = false;
      })
      .addCase(attachBoxToCoupon.pending, (state) => {
        state.attachBoxStatus = 'loading';
        state.attachBoxError = null;
      })
      .addCase(attachBoxToCoupon.fulfilled, (state, action) => {
        const { coupon_id, box_id } = action.payload;
        const couponIndex = state.coupons.findIndex(coupon => coupon.id === coupon_id);
        if (couponIndex !== -1) {
          state.coupons[couponIndex].box_id = box_id;
        }
        state.attachBoxStatus = 'succeeded';
      })
      .addCase(attachBoxToCoupon.rejected, (state, action) => {
        state.attachBoxStatus = 'failed';
        state.attachBoxError = action.payload || 'Failed to attach box to coupon';
      });
    builder.addCase(deleteCoupon.pending, (state, action) => {
      state.deleteCouponStatus = 'loading';
      state.deleteCouponError = null;
      state.isDeleteCouponInprogress = true;
      state.deleteCouponId = action.meta.arg; // Store the couponId being deleted
    });
    builder.addCase(deleteCoupon.fulfilled, (state) => {
      state.deleteCouponStatus = 'succeeded';
      state.isDeleteCouponInprogress = false;
      state.deleteCouponId = null;
    });
    builder.addCase(deleteCoupon.rejected, (state, action) => {
      state.deleteCouponStatus = 'failed';
      state.deleteCouponError = action.payload || 'Failed to delete coupon';
      state.isDeleteCouponInprogress = false;
    });
  },
});

export const { setCurrentPage, resetAttachBoxToCouponStatus, resetDeleteWalletStatus, resetGenerateCouponsStatus } = couponsSlice.actions;

export default couponsSlice.reducer;
